/**
 * AppBuilderApplication - Application class for AppBuilderApp
 * Manages multi-file code projects with Y.js and BaseApplication
 */

import * as Y from 'yjs';
import React from 'react';
import { WebsocketProvider } from 'y-websocket';
import { BaseApplication } from '../../../services/baseApplication/BaseApplication';
import { AppConfig } from '../../../services/baseApplication/types';
import { entityService } from '../../../services/entityService';
import { WEBSOCKET_URL } from '../../../api/config';
import { createSandboxedAPI } from '../../../services/sandboxedRiverAPI';

// App configuration
const APP_BUILDER_CONFIG: AppConfig = {
  appId: 'appbuilder',
  appName: 'appbuilder',
  containerType: 'page',
  layoutMode: 'flow',
  supportedClasses: {}, // Not using ProseMirror nodes, using Y.Map for files
  supportedPositionTypes: ['flow'],
  supportsDragDrop: false,
  supportsResize: false,
  supportsRotation: false,
  editorSelector: '.appbuilder-editor'
};

export class AppBuilderApplication extends BaseApplication {
  // File maps
  public filesMap!: Y.Map<Y.Text>;
  public errorsMap!: Y.Map<any>;
  public metadataMap!: Y.Map<any>;

  // Volatile error state — populated by AppBuilderApp.tsx, read by AI commands
  public lastCompilationError: string | null = null;
  public recentRuntimeErrors: string[] = []; // capped at 20 entries
  
  // Entity reference
  public entity: any = null;

  // Cleanup function returned by setupCapabilityDetection — called in destroy()
  private cleanupCapabilityDetection: (() => void) | null = null;
  
  // Public Y.doc accessor
  public get ydocPublic(): Y.Doc | undefined {
    return this.ydoc;
  }
  
  constructor() {
    super();
    this.suggestionMode = false; // No suggestion mode for code
  }
  
  /**
   * Get application configuration
   */
  protected getConfig(): AppConfig {
    return APP_BUILDER_CONFIG;
  }
  
  /**
   * Load project from backend
   */
  async load(entityId: string, options: { offline?: boolean } = {}): Promise<void> {
    console.log('[AppBuilderApplication] Loading project:', entityId);
    
    // Fetch entity
    this.entity = await entityService.get(entityId);
    if (!this.entity) {
      throw new Error(`Project not found: ${entityId}`);
    }
    
    console.log('[AppBuilderApplication] Entity fetched:', {
      name: this.entity.name,
      entityType: this.entity.entityType,
      hasBase64: !!this.entity.skeleton?.base64
    });
    
    // Initialize base application
    await this.initialize(entityId);
    
    // Create Y.Doc (self-contained)
    this.ydoc = new Y.Doc();
    console.log('[AppBuilderApplication] Y.Doc created, clientID:', this.ydoc.clientID);
    
    // Load base64 state if exists
    if (this.entity.skeleton?.base64 && this.ydoc) {
      console.log('[AppBuilderApplication] Loading base64 state...');
      try {
        this.loadBase64State(this.entity.skeleton.base64);
        console.log('[AppBuilderApplication] Base64 state loaded');
      } catch (error) {
        console.error('[AppBuilderApplication] Failed to load Y.js state:', error);
        console.log('[AppBuilderApplication] Starting with empty project');
      }
    }
    
    // Get Y.js maps
    this.filesMap = this.ydoc.getMap('files');
    this.errorsMap = this.ydoc.getMap('errors');
    this.metadataMap = this.ydoc.getMap('metadata');
    
    console.log('[AppBuilderApplication] Got Y.js maps:', {
      files: this.filesMap.size,
      errors: this.errorsMap.size
    });
    
    // Initialize default file if empty
    if (this.filesMap.size === 0 && !this.entity.skeleton?.base64) {
      console.log('[AppBuilderApplication] Empty project, creating default file...');
      this.initializeDefaultFile();
    }
    
    // Setup auto-save
    console.log('[AppBuilderApplication] Setting up auto-save...');
    this.setupAutoSave();
    
    // Setup capability detection (store cleanup so destroy() can tear it down)
    console.log('[AppBuilderApplication] Setting up capability detection...');
    const { setupCapabilityDetection } = await import('./capabilityDetection');
    this.cleanupCapabilityDetection = setupCapabilityDetection(this);
    
    // Setup collaboration (optional)
    if (!options.offline && this.ydoc) {
      console.log('[AppBuilderApplication] Setting up collaboration...');
      await this.setupCollaboration(entityId);
    }
    
    console.log('[AppBuilderApplication] Load complete');
  }
  
  /**
   * Initialize default file for new projects
   */
  private initializeDefaultFile(): void {
    const defaultCode = `import React from 'react';

interface AppProps {
  windowId: string;
  spaceId: string;
}

const styles: Record<string, React.CSSProperties> = {
  shell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    background: '#f9fafb',
    padding: '24px',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '12px',
    maxWidth: '360px',
  },
  icon: { fontSize: '32px', color: '#6366f1', marginBottom: '4px' },
  title: { fontSize: '18px', fontWeight: 600, color: '#111827', letterSpacing: '-0.01em' },
  subtitle: { fontSize: '14px', color: '#6b7280', lineHeight: '1.6' },
};

export const App: React.FC<AppProps> = () => {
  return (
    <div style={styles.shell}>
      <div style={styles.card}>
        <div style={styles.icon}>✦</div>
        <h1 style={styles.title}>Your app will appear here</h1>
        <p style={styles.subtitle}>Describe what you want to build and the AI will generate it for you.</p>
      </div>
    </div>
  );
};

export default App;
`;
    
    const ytext = new Y.Text();
    ytext.insert(0, defaultCode);
    this.filesMap.set('src/App.tsx', ytext);
    
    console.log('[AppBuilderApplication] Created default src/App.tsx');
  }
  
  /**
   * Get file content
   */
  getFile(path: string): Y.Text | undefined {
    return this.filesMap.get(path);
  }
  
  /**
   * Create new file
   */
  createFile(path: string, content: string = ''): Y.Text {
    const ytext = new Y.Text();
    if (content) {
      ytext.insert(0, content);
    }
    this.filesMap.set(path, ytext);
    
    console.log('[AppBuilderApplication] Created file:', path);
    return ytext;
  }
  
  /**
   * Delete file
   */
  deleteFile(path: string): void {
    this.filesMap.delete(path);
    // Also remove errors for this file
    this.errorsMap.delete(path);
    
    console.log('[AppBuilderApplication] Deleted file:', path);
  }
  
  /**
   * Rename file
   */
  renameFile(oldPath: string, newPath: string): void {
    const ytext = this.filesMap.get(oldPath);
    if (ytext) {
      this.filesMap.set(newPath, ytext);
      this.filesMap.delete(oldPath);
      
      // Move errors too
      const errors = this.errorsMap.get(oldPath);
      if (errors) {
        this.errorsMap.set(newPath, errors);
        this.errorsMap.delete(oldPath);
      }
      
      console.log('[AppBuilderApplication] Renamed file:', oldPath, '->', newPath);
    }
  }
  
  /**
   * Get all files as plain objects
   */
  getAllFiles(): Map<string, string> {
    const files = new Map<string, string>();
    this.filesMap.forEach((ytext, path) => {
      files.set(path, ytext.toString());
    });
    return files;
  }
  
  /**
   * Get all file paths
   */
  getAllFilePaths(): string[] {
    return Array.from(this.filesMap.keys());
  }
  
  /**
   * Get files as plain object for compilation
   */
  getFilesForCompilation(): Record<string, string> {
    const files: Record<string, string> = {};
    this.filesMap.forEach((ytext, path) => {
      files[path] = ytext.toString();
    });
    return files;
  }
  
  // No feature flag - always use server bundling
  // Server bundling is the only supported path for production
  
  /**
   * Compile project — routes to website or app pipeline based on projectKind.
   * @param page For website projects: which HTML file to compile (default: 'index.html')
   */
  async compile(page?: string): Promise<{ success: boolean; Component?: React.ComponentType<any>; compiledCode?: string; css?: string; error?: string; warning?: string; generatedFiles?: Record<string, string> }> {
    const projectKind = this.entity?.skeleton?.projectKind;
    const entityType = this.entity?.entityType;

    // Use entityType as a reliable fallback when projectKind is missing from the skeleton
    const isWebsite = projectKind === 'website'
      || entityType === 'website_project'
      || entityType === 'WebsiteProject';

    console.log('[AppBuilderApplication] compile() routing', {
      projectKind,
      entityType,
      isWebsite,
      page,
    });

    if (isWebsite) {
      return this.compileWebsite(page ?? 'index.html');
    }
    if (projectKind === 'nextjs') {
      return this.compileNextJS();
    }
    return this.compileApp();
  }

  /**
   * Next.js compile: synthesise a single SPA bundle with a pathname-based
   * client-side router, wrap it as a full HTML document, and serve it through
   * the existing service worker VFS — exactly as website projects are served.
   *
   * Limitations: CSR only (no SSR/API routes). Full features require Vercel deploy.
   */
  private async compileNextJS(): Promise<{ success: boolean; compiledCode?: string; css?: string; error?: string; warning?: string; generatedFiles?: Record<string, string> }> {
    console.log('[AppBuilderApplication] Compiling Next.js project (SPA preview mode)...');

    const files = this.getFilesForCompilation();

    try {
      // ── Step 1: Strip Next.js-specific syntax from all TS/TSX files ──────
      const modifiedFiles: Record<string, string> = {};

      for (const [path, content] of Object.entries(files)) {
        if (path.endsWith('.tsx') || path.endsWith('.ts')) {
          // Skip API routes and layout (layout renders <html><body> which breaks nested preview)
          if (path.startsWith('app/api/') || path.startsWith('pages/api/')) continue;
          if (path === 'app/layout.tsx' || path === 'app/layout.ts') continue;

          let modified = content;

          // ── Shim specific next/* modules before the catch-all ──────────────
          // next/image → plain <img> wrapper
          modified = modified.replace(
            /import\s+(\w+)\s+from\s+['"]next\/image['"]\s*;?/g,
            (_m, name) => `const ${name} = ({ src, alt, width, height, fill, priority, quality, placeholder, blurDataURL, loader, unoptimized, ...rest }) => React.createElement('img', { src, alt, width: fill ? '100%' : width, height: fill ? '100%' : height, ...rest });`
          );

          // next/link → plain <a> wrapper
          modified = modified.replace(
            /import\s+(\w+)\s+from\s+['"]next\/link['"]\s*;?/g,
            (_m, name) => `const ${name} = ({ href, children, prefetch, scroll, shallow, replace, locale, ...rest }) => React.createElement('a', { href, ...rest }, children);`
          );

          // next/navigation named imports → stub hooks
          modified = modified.replace(
            /import\s+\{([^}]+)\}\s+from\s+['"]next\/navigation['"]\s*;?/g,
            (_m, imports) => imports.split(',').map((s: string) => {
              const name = s.trim();
              if (name === 'useRouter') return `const useRouter = () => ({ push: (p) => { window.location.href = '/__preview' + p; }, replace: (p) => { window.location.href = '/__preview' + p; }, back: () => window.history.back(), forward: () => window.history.forward(), refresh: () => window.location.reload(), prefetch: () => {} });`;
              if (name === 'usePathname') return `const usePathname = () => window.location.pathname.replace(/^\\/__preview/, '') || '/';`;
              if (name === 'useSearchParams') return `const useSearchParams = () => new URLSearchParams(window.location.search);`;
              if (name === 'useParams') return `const useParams = () => ({});`;
              if (name === 'redirect') return `const redirect = (p) => { window.location.href = '/__preview' + p; };`;
              if (name === 'notFound') return `const notFound = () => null;`;
              return `const ${name} = () => null;`;
            }).join('\n')
          );

          // next/font/* → stub returning empty className
          modified = modified.replace(
            /import\s+\{([^}]+)\}\s+from\s+['"]next\/font\/[^'"]*['"]\s*;?/g,
            (_m, imports) => imports.split(',').map((s: string) => {
              const name = s.trim();
              return `const ${name} = (_opts) => ({ className: '', style: {}, variable: '' });`;
            }).join('\n')
          );

          // next/headers → server-only stubs (no-op)
          modified = modified.replace(
            /import\s+\{[^}]+\}\s+from\s+['"]next\/headers['"]\s*;?/g,
            '/* next/headers not available in CSR preview */'
          );

          // Catch-all: any remaining next/* imports → remove (side-effect imports) or from 'react'
          modified = modified
            .replace(/from ['"]next\/[^'"]*['"]/g, "from 'react'")
            .replace(/import\s+['"]next\/[^'"]*['"]\s*;?\n?/g, '');

          // ── Remove server-only exports ──────────────────────────────────────
          modified = modified
            .replace(/^export const metadata\s*=[\s\S]*?(?=\nexport|\nconst|\nfunction|\nclass|\ndefault|$)/gm, '')
            .replace(/^export const revalidate\s*=.*$/gm, '')
            .replace(/^export const dynamic\s*=.*$/gm, '')
            // Server components use async functions — make them sync for CSR
            .replace(/^export default async function/gm, 'export default function')
            .replace(/^async function/gm, 'function')
            .replace(/^['"]use server['"]\s*;?\s*\n/gm, '');

          // Resolve @/ path aliases relative to file depth
          const dirDepth = path.split('/').length - 1;
          const upPath = dirDepth > 0 ? '../'.repeat(dirDepth) : './';
          modified = modified.replace(/from ['"]@\/([^'"]+)['"]/g, `from '${upPath}$1'`);

          // Remove CSS imports (handled separately)
          modified = modified.replace(/import\s+['"][^'"]*\.css['"]\s*;?\n?/g, '');
          modified = modified.replace(/import\s+['"][^'"]*\.scss['"]\s*;?\n?/g, '');

          modifiedFiles[path] = modified;
        } else if (path.endsWith('.css') || path.endsWith('.scss')) {
          modifiedFiles[path] = content;
        } else if (!path.endsWith('.mdx') && !path.endsWith('.example') && !path.endsWith('.env.local.example')) {
          modifiedFiles[path] = content;
        }
      }

      // ── Step 2: Discover page routes ──────────────────────────────────────
      // Supports app/ router (page.tsx) and pages/ router
      interface RouteEntry { importPath: string; varName: string; routePrefix: string }
      const routes: RouteEntry[] = [];

      const allPaths = Object.keys(modifiedFiles);

      // app/ router: any file matching app/**/page.tsx (or .ts/.jsx/.js)
      for (const filePath of allPaths) {
        const appMatch = filePath.match(/^app(\/.*)?\/page\.[tj]sx?$/);
        if (!appMatch) continue;

        // Dynamic route segments (e.g. [slug]) — skip for now, handled as catch-all
        if (filePath.includes('[')) continue;

        const dir = filePath.replace(/\/page\.[tj]sx?$/, '').replace(/^app/, '') || '/';
        const routePrefix = dir === '/' ? '/' : dir;
        const varName = 'Page' + routePrefix.replace(/\//g, '_').replace(/[^a-zA-Z0-9_]/g, '') || 'PageRoot';
        const importPath = '../' + filePath.replace(/\.[tj]sx?$/, '');

        routes.push({ importPath, varName, routePrefix });
      }

      // pages/ router: any .tsx/.ts file (not _app, _document, api/)
      for (const filePath of allPaths) {
        if (!filePath.startsWith('pages/')) continue;
        if (filePath.startsWith('pages/api/')) continue;
        if (filePath.match(/pages\/_[^/]+\.[tj]sx?$/)) continue;
        if (filePath.includes('[')) continue;

        const routePart = filePath
          .replace(/^pages/, '')
          .replace(/\.[tj]sx?$/, '')
          .replace(/\/index$/, '');
        const routePrefix = routePart || '/';
        const varName = 'Page' + routePrefix.replace(/\//g, '_').replace(/[^a-zA-Z0-9_]/g, '') || 'PageRoot';
        const importPath = '../' + filePath.replace(/\.[tj]sx?$/, '');

        routes.push({ importPath, varName, routePrefix });
      }

      // Always need at least one page
      if (routes.length === 0) {
        return { success: false, error: 'No page files found (expected app/page.tsx or pages/index.tsx)' };
      }

      // Sort so root comes first, then longest prefix last (more specific wins)
      routes.sort((a, b) => {
        if (a.routePrefix === '/') return -1;
        if (b.routePrefix === '/') return 1;
        return b.routePrefix.length - a.routePrefix.length;
      });

      // ── Step 3: Synthesise the router entry ───────────────────────────────
      // Layout is intentionally skipped — it renders <html><body> which conflicts
      // with the preview document's own html/body structure.
      const imports = routes
        .map(r => `import ${r.varName} from '${r.importPath}';`)
        .join('\n');

      // Build if/else chain: most-specific routes first (already sorted)
      const routeChecks = routes
        .filter(r => r.routePrefix !== '/')
        .map(r => `  if (route.startsWith('${r.routePrefix}')) return ${r.varName};`)
        .join('\n');

      const rootRoute = routes.find(r => r.routePrefix === '/');
      const fallbackComponent = rootRoute ? rootRoute.varName : routes[routes.length - 1].varName;

      const syntheticEntry = `import React, { useState, useEffect } from 'react';
${imports}

function resolveComponent() {
  const route = window.location.pathname.replace(/^\\/__preview/, '') || '/';
${routeChecks}
  return ${fallbackComponent};
}

function NextJSPreviewRouter() {
  const [CurrentPage, setCurrentPage] = useState(resolveComponent);

  useEffect(() => {
    const onPopState = () => setCurrentPage(resolveComponent);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  return React.createElement(CurrentPage, null);
}

export default NextJSPreviewRouter;
`;

      modifiedFiles['src/App.tsx'] = syntheticEntry;

      // ── Step 5: Bundle ─────────────────────────────────────────────────────
      const { serverBundler } = await import('./serverBundler');

      const cssCode = Object.entries(modifiedFiles)
        .filter(([p]) => p.endsWith('.css') || p.endsWith('.scss'))
        .map(([, c]) => c)
        .join('\n\n');

      console.log('[AppBuilderApplication] Next.js routes found:', routes.map(r => r.routePrefix));
      console.log('[AppBuilderApplication] Bundling Next.js SPA...');

      const bundleResult = await serverBundler.bundle(modifiedFiles);

      if (!bundleResult.success || !bundleResult.code) {
        return { success: false, error: bundleResult.error || 'Next.js compilation failed' };
      }

      // ── Step 6: Wrap in HTML and return as service worker VFS files ─────────
      const { generatePreviewHTML } = await import('../../../components/BuilderKit/LivePreview/generatePreviewHTML');

      // omitBridge: VFS will inject its own bridge via injectBridgeScript() — avoid double injection
      // embedCode: HTML is stored in VFS (no parent can send preview-init), code goes in a data island
      const html = generatePreviewHTML({ compiledCode: bundleResult.code, cssCode, omitBridge: true, embedCode: true });

      console.log('[AppBuilderApplication] Next.js SPA compiled, serving via service worker VFS');

      return {
        success: true,
        compiledCode: '@@SERVICE_WORKER_MODE@@',
        css: '',
        generatedFiles: { 'index.html': html },
        warning: 'Preview is CSR only. Deploy to Vercel for full SSR/API routes.',
      };

    } catch (error: any) {
      console.error('[AppBuilderApplication] Next.js compilation error:', error);
      return { success: false, error: error.message || 'Next.js compilation failed' };
    }
  }

  /**
   * Website compile: prepare files for service worker virtual filesystem.
   * 
   * Instead of creating a single srcDoc HTML blob, we:
   * 1. Return a marker indicating service worker mode
   * 2. The parent component will send all files to the service worker
   * 3. The iframe navigates to /__preview/index.html
   * 4. Service worker serves files from in-memory VFS
   * 
   * This enables real browser navigation with proper URL handling.
   */
  private async compileWebsite(page: string = 'index.html'): Promise<{ success: boolean; compiledCode?: string; css?: string; error?: string; mode?: string; currentPage?: string }> {
    const files = this.getFilesForCompilation();
    const fileList = Object.keys(files);
    console.log('[AppBuilderApplication] compileWebsite() entering service-worker mode', {
      requestedPage: page,
      fileCount: fileList.length,
      files: fileList,
    });

    try {
      // Check if the requested page exists
      if (!files[page]) {
        if (!files['index.html']) {
          console.error('[AppBuilderApplication] compileWebsite() — no index.html found in', fileList);
          return { success: false, error: 'No index.html found' };
        }
        console.warn('[AppBuilderApplication] compileWebsite() — requested page not found, falling back to index.html', { requestedPage: page });
        page = 'index.html';
      }

      console.log('[AppBuilderApplication] compileWebsite() ✅ returning SERVICE_WORKER_MODE marker for page:', page);

      return { 
        success: true, 
        compiledCode: '@@SERVICE_WORKER_MODE@@',
        mode: 'service-worker',
        currentPage: page,
        css: '' 
      };
    } catch (error: any) {
      console.error('[AppBuilderApplication] compileWebsite() error:', error);
      return { success: false, error: error.message || 'Website compile failed' };
    }
  }

  /**
   * App compile — server-side bundling (original path, now extracted to named method)
   */
  private async compileApp(): Promise<{ success: boolean; Component?: React.ComponentType<any>; compiledCode?: string; css?: string; error?: string }> {
    console.log('[AppBuilderApplication] Compiling with server-side bundler...');
    
    const files = this.getFilesForCompilation();
    
    try {
      const { serverBundler } = await import('./serverBundler');
      
      // Extract CSS files
      const cssFiles = Object.entries(files).filter(([path]) => 
        path.endsWith('.css') || path.endsWith('.scss')
      );
      
      // Combine CSS for iframe
      const cssCode = cssFiles.map(([, content]) => content).join('\n\n');
      
      console.log('[AppBuilderApplication] Sending to server for bundling...');
      
      // Bundle on server with real npm packages
      const bundleResult = await serverBundler.bundle(files);
      
      if (!bundleResult.success || !bundleResult.code) {
        console.error('[AppBuilderApplication] Server bundling failed:', bundleResult.error);
        return {
          success: false,
          error: bundleResult.error || 'Server bundling failed'
        };
      }
      
      console.log('[AppBuilderApplication] ✅ Server bundle received:', bundleResult.bundleSize, 'bytes');
      
      // Return compiled code for iframe execution (safer than creating component)
      return {
        success: true,
        compiledCode: bundleResult.code,
        css: cssCode
      };
      
    } catch (error: any) {
      console.error('[AppBuilderApplication] Server bundling error:', error);
      return {
        success: false,
        error: error.message || 'Server bundling failed'
      };
    }
  }
  
  /**
   * Create component from server-bundled code (simpler than multi-file module system)
   */
  private async createComponentFromBundledCode(code: string, _files: Record<string, string>): Promise<React.ComponentType<any>> {
    try {
      const api = createSandboxedAPI({
        spaceId: this.entity?.spaceId || '',
        userId: 'current-user',
        appId: 'app-builder-preview',
        toolId: undefined
      });
      
      const { useState, useEffect, useCallback, useMemo, useRef, useContext } = React;
      
      // Server bundle is already a complete CommonJS module
      // Just execute it with proper exports structure
      const AsyncFunction = Object.getPrototypeOf(async function(){}).constructor;
      
      const componentFactory = new AsyncFunction(
        'React',
        'useState',
        'useEffect',
        'useCallback',
        'useMemo',
        'useRef',
        'useContext',
        'river',
        `
        const exports = {};
        const module = { exports };
        
        ${code}
        
        // Return the App component
        if (module.exports !== exports) {
          return module.exports;
        }
        return exports.default || exports.App || (typeof App !== 'undefined' ? App : null);
        `
      );
      
      const Component = await componentFactory(
        React,
        useState,
        useEffect,
        useCallback,
        useMemo,
        useRef,
        useContext,
        api
      );
      
      if (!Component) {
        throw new Error('No App component exported from bundled code');
      }
      
      console.log('[AppBuilderApplication] Component created from server bundle');
      return Component;
      
    } catch (error: any) {
      console.error('[AppBuilderApplication] Error creating component from bundle:', error);
      throw new Error(`Component creation failed: ${error.message}`);
    }
  }
  
  /**
   * Inject CSS into the document with scoping to prevent conflicts
   * (Legacy - only used if component mode is ever needed)
   */
  private injectCSS(cssFiles: Array<[string, string]>): void {
    // Remove old injected styles for this project
    const oldStyle = document.getElementById(`app-builder-styles-${this.entityId}`);
    if (oldStyle) {
      oldStyle.remove();
    }
    
    // Create unique scope class
    const scopeClass = `app-preview-${this.entityId?.substring(0, 8)}`;
    
    // Combine and scope all CSS
    const scopedCSS = cssFiles.map(([path, content]) => {
      const scoped = this.scopeCSS(content, scopeClass);
      return `/* ${path} */\n${scoped}`;
    }).join('\n\n');
    
    // Inject new style tag
    const styleElement = document.createElement('style');
    styleElement.id = `app-builder-styles-${this.entityId}`;
    styleElement.textContent = scopedCSS;
    document.head.appendChild(styleElement);
    
    console.log('[AppBuilderApplication] Injected scoped CSS:', cssFiles.map(([path]) => path).join(', '));
    console.log('[AppBuilderApplication] Scope class:', scopeClass);
    
    // Store scope class for component wrapper
    this.cssScope = scopeClass;
  }
  
  /**
   * Scope CSS to prevent conflicts with River UI
   */
  private scopeCSS(css: string, scopeClass: string): string {
    // Parse CSS and prefix all selectors
    return css.replace(/([^\r\n,{}]+)(,(?=[^}]*{)|\s*{)/g, (match, selector, separator) => {
      // Trim selector
      const trimmed = selector.trim();
      
      // Skip @rules, keyframes, etc.
      if (trimmed.startsWith('@') || trimmed.startsWith('from') || trimmed.startsWith('to') || trimmed.match(/^\d+%$/)) {
        return match;
      }
      
      // Scope the selector
      return `.${scopeClass} ${trimmed}${separator}`;
    });
  }
  
  /**
   * Get CSS scope class for this project
   */
  public getCSSScope(): string {
    return this.cssScope || `app-preview-${this.entityId?.substring(0, 8)}`;
  }
  
  private cssScope: string = '';
  
  /**
   * Create React component from compiled code with module system
   */
  private async createComponentFromCode(code: string, files: Record<string, string>): Promise<React.ComponentType<any>> {
    try {
      // Create sandboxed API
      const api = createSandboxedAPI({
        spaceId: this.entity?.spaceId || '',
        userId: 'current-user',
        appId: 'app-builder-preview',
        toolId: undefined
      });
      
      // Make React hooks available
      const { useState, useEffect, useCallback, useMemo, useRef, useContext } = React;
      
      // Compile all TypeScript files to JavaScript
      const { runtimeCompiler } = await import('./runtimeCompiler');
      const compiledModules: Record<string, string> = {};
      
      const startInfo = {
        totalFiles: Object.keys(files).length,
        tsFiles: Object.keys(files).filter(p => p.endsWith('.tsx') || p.endsWith('.ts')),
        cssFiles: Object.keys(files).filter(p => p.endsWith('.css'))
      };
      console.log('[AppBuilderApplication] 📦 Starting module compilation:', JSON.stringify(startInfo, null, 2));
      
      for (const [path, content] of Object.entries(files)) {
        if (path.endsWith('.tsx') || path.endsWith('.ts')) {
          console.log(`[AppBuilderApplication] 🔨 Compiling: ${path}`);
          const result = await runtimeCompiler.compileApp({ [path]: content }, path);
          if (result.success && result.code) {
            compiledModules[path] = result.code;
            const compileInfo = {
              inputLength: content.length,
              outputLength: result.code.length,
              first100: result.code.substring(0, 100)
            };
            console.log(`[AppBuilderApplication] ✅ Compiled ${path}:`, JSON.stringify(compileInfo, null, 2));
          } else {
            console.error(`[AppBuilderApplication] ❌ Failed to compile ${path}:`, result.errors);
          }
        }
      }
      
      const moduleInfo = {
        count: Object.keys(compiledModules).length,
        paths: Object.keys(compiledModules)
      };
      console.log('[AppBuilderApplication] 📋 Compiled modules:', JSON.stringify(moduleInfo, null, 2));
      
      // Create simple module system
      const moduleCache: Record<string, any> = {};
      
      const requireModule = async (modulePath: string, fromPath: string = 'src/App.tsx') => {
        console.log('[requireModule] 📞 Called with:', { modulePath, fromPath });
        
        // Handle npm packages (bare imports like 'react-window', 'lodash', etc.)
        if (!modulePath.startsWith('./') && !modulePath.startsWith('../') && !modulePath.startsWith('/')) {
          // Check cache first
          const cacheKey = `npm:${modulePath}`;
          if (moduleCache[cacheKey]) {
            console.log('[requireModule] ♻️ Using cached npm module:', modulePath);
            return moduleCache[cacheKey];
          }
          
          console.log('[requireModule] 📦 Loading npm package:', modulePath);
          
          try {
            // Construct the URL
            const url = `https://esm.sh/${modulePath}@latest`;
            console.log('[requireModule] 🌐 Importing from:', url);
            
            // Load from ESM.sh CDN
            // Use Function constructor to avoid webpack static analysis
            const dynamicImport = new Function('url', 'return import(url)');
            const mod = await dynamicImport(url);
            
            // Log detailed module structure
            const moduleKeys = Object.keys(mod);
            console.log('[requireModule] ✅ Loaded npm package:', modulePath);
            console.log('[requireModule] ALL Module keys:', moduleKeys);
            console.log('[requireModule] Has default?', !!mod.default);
            console.log('[requireModule] Has __esModule?', !!mod.__esModule);
            console.log('[requireModule] Has FixedSizeList?', !!mod.FixedSizeList);
            console.log('[requireModule] Has List?', !!mod.List);
            if (mod.default) {
              console.log('[requireModule] default keys:', Object.keys(mod.default));
            }
            
            // For CommonJS interop, Babel expects named exports to be directly accessible
            // ESM modules from import() already have this structure, so we can return as-is
            moduleCache[cacheKey] = mod;
            return mod;
          } catch (error: any) {
            console.error('[requireModule] ❌ Failed to load npm package:', modulePath, error);
            throw new Error(`Failed to load npm package '${modulePath}': ${error.message}`);
          }
        }
        
        // Handle local file imports
        // Normalize path
        let normalizedPath = modulePath;
        
        if (modulePath.startsWith('./') || modulePath.startsWith('../')) {
          // Get directory of the importing file
          const fromDir = fromPath.substring(0, fromPath.lastIndexOf('/'));
          
          // Resolve relative path
          const parts = fromDir.split('/');
          const pathParts = modulePath.split('/');
          
          for (const part of pathParts) {
            if (part === '..') {
              parts.pop(); // Go up one directory
            } else if (part === '.') {
              // Current directory, do nothing
            } else if (part) {
              parts.push(part);
            }
          }
          
          normalizedPath = parts.join('/');
          
          // Add extension if missing
          if (!normalizedPath.endsWith('.ts') && !normalizedPath.endsWith('.tsx')) {
            if (compiledModules[normalizedPath + '.ts']) {
              normalizedPath = normalizedPath + '.ts';
            } else if (compiledModules[normalizedPath + '.tsx']) {
              normalizedPath = normalizedPath + '.tsx';
            }
          }
        }
        
        const resolveInfo = {
          modulePath,
          fromPath,
          normalizedPath,
          cached: !!moduleCache[normalizedPath],
          exists: !!compiledModules[normalizedPath]
        };
        console.log('[requireModule] 📍 Resolving:', JSON.stringify(resolveInfo, null, 2));
        
        // Check cache
        if (moduleCache[normalizedPath]) {
          console.log('[requireModule] ♻️ Using cached module:', normalizedPath);
          return moduleCache[normalizedPath];
        }
        
        // Get compiled code
        const moduleCode = compiledModules[normalizedPath];
        if (!moduleCode) {
          const notFoundInfo = {
            modulePath,
            normalizedPath,
            availableModules: Object.keys(compiledModules)
          };
          console.error('[requireModule] ❌ Module not found:', JSON.stringify(notFoundInfo, null, 2));
          throw new Error(`Module not found: ${modulePath} (resolved to ${normalizedPath})`);
        }
        
        // Log full code for VariableList to debug react-window issue
        if (normalizedPath.includes('VariableList')) {
          console.log('[requireModule] 🔧 FULL VariableList code:', moduleCode);
        }
        
        const execInfo = {
          normalizedPath,
          codeLength: moduleCode.length,
          first800: moduleCode.substring(0, 800)
        };
        console.log('[requireModule] 🔧 Executing module:', JSON.stringify(execInfo, null, 2));
        
        // Execute module with proper export handling (async for await import)
        // Use AsyncFunction to support top-level await
        const AsyncFunction = Object.getPrototypeOf(async function(){}).constructor;
        const moduleExports: any = {};
        const moduleFactory = new AsyncFunction(
          'exports',
          'module',
          'require',
          'React',
          'useState',
          'useEffect',
          'useCallback',
          'useMemo',
          'useRef',
          'useContext',
          'river',
          `
          ${moduleCode}
          return exports;
          `
        );
        
        const moduleObj = { exports: moduleExports };
        
        // Create require that remembers current module path
        const boundRequire = (path: string) => requireModule(path, normalizedPath);
        
        const resultPromise = moduleFactory(
          moduleExports,
          moduleObj,
          boundRequire,
          React,
          useState,
          useEffect,
          useCallback,
          useMemo,
          useRef,
          useContext,
          api
        );
        
        // Await async module execution (for ESM.sh imports)
        const result = await resultPromise;
        
        // Return module.exports if it was reassigned, otherwise use exports object
        const finalExports = moduleObj.exports !== moduleExports ? moduleObj.exports : result;
        
        console.log('[requireModule] ✅ Module executed:', JSON.stringify({
          normalizedPath,
          hasResult: !!result,
          hasModuleExports: !!moduleObj.exports,
          moduleExportsKeys: Object.keys(moduleObj.exports || {}),
          resultKeys: Object.keys(result || {}),
          finalExportsKeys: Object.keys(finalExports || {}),
          usingModuleExports: moduleObj.exports !== moduleExports
        }, null, 2));
        
        moduleCache[normalizedPath] = finalExports;
        return finalExports;
      };
      
      // Create async function that supports dynamic imports (for ESM.sh)
      // Use AsyncFunction constructor to allow top-level await
      const AsyncFunction = Object.getPrototypeOf(async function(){}).constructor;
      
      const componentFactory = new AsyncFunction(
        'React',
        'useState',
        'useEffect',
        'useCallback',
        'useMemo',
        'useRef',
        'useContext',
        'river',
        'require',
        `
        const exports = {};
        const module = { exports };
        
        ${code}
        
        // Return the App component
        // Check: module.exports (if reassigned), exports.default (Babel default export), exports.App, or global App
        if (module.exports !== exports) {
          return module.exports;
        }
        return exports.default || exports.App || (typeof App !== 'undefined' ? App : null);
        `
      );
      
      // Execute to get component (now async for dynamic imports)
      const ComponentPromise = componentFactory(
        React,
        useState,
        useEffect,
        useCallback,
        useMemo,
        useRef,
        useContext,
        api,
        requireModule
      );
      
      // Wait for async imports to resolve
      const Component = await ComponentPromise;
      
      if (!Component) {
        throw new Error('No App component exported from code');
      }
      
      console.log('[AppBuilderApplication] Component created successfully with module system');
      
      return Component;
    } catch (error: any) {
      console.error('[AppBuilderApplication] Error creating component:', error);
      
      // Create detailed error for AI to understand and fix
      const errorMessage = [
        `Component Creation Error`,
        ``,
        `Error: ${error.message}`,
        ``,
        `File: src/App.tsx (${files['src/App.tsx']?.split('\n').length || 0} lines)`,
        ``,
        `This usually means:`,
        `1. Syntax error in the code (check for unclosed brackets, parentheses)`,
        `2. JSX not transpiled (make sure Babel is loaded)`,
        `3. TypeScript syntax not fully removed`,
        ``,
        `Stack: ${error.stack?.split('\n').slice(0, 3).join('\n') || 'N/A'}`,
        ``,
        `Code preview (first 500 chars):`,
        code.substring(0, 500) + '...',
      ].join('\n');
      
      throw new Error(errorMessage);
    }
  }
  
  /**
   * Setup collaboration
   */
  private async setupCollaboration(entityId: string): Promise<void> {
    if (!this.ydoc) return;

    const entityType = this.entity?.entityType || 'app_project';
    const roomName = `${entityType}:${entityId}`;

    const provider = new WebsocketProvider(WEBSOCKET_URL, roomName, this.ydoc, { connect: true });

    // Wait up to 5s for initial sync; don't block load on slow/offline network
    await new Promise<void>((resolve) => {
      if (provider.synced) { resolve(); return; }
      const timeout = setTimeout(resolve, 5000);
      provider.on('sync', (synced: boolean) => {
        if (synced) { clearTimeout(timeout); resolve(); }
      });
    });

    console.log(`[AppBuilderApplication] WebSocket collaboration active: ${roomName}`);
  }
  
  /**
   * Clean up
   */
  async destroy(): Promise<void> {
    console.log('[AppBuilderApplication] Destroying...');
    if (this.cleanupCapabilityDetection) {
      this.cleanupCapabilityDetection();
      this.cleanupCapabilityDetection = null;
    }
    await super.destroy();
  }
}
